import { useMemo } from 'react';
import { ToothPeriodontal, ToothSide } from '../../../../../graphql/graphql';
import { ToothView } from '../../../Enums/ToothView.enum';
import { PeriodontalToothView } from '../../../Interfaces/PeriodontalToothView.type';

type ValueType = 'margin' | 'depth';
type ValuesMap = Record<
	ValueType,
	Record<ToothSide, Record<PeriodontalToothView, number[]>>
>;

interface Props {
	periodontal?: ToothPeriodontal;
	view: PeriodontalToothView;
	side: ToothSide;
}

export const DEFAULT_PERIODONTAL_DATA = {
	margins: [0, 0, 0],
	depths: [0, 0, 0],
};

export default function usePeriodontalData({ periodontal, view, side }: Props) {
	if (!periodontal) {
		return DEFAULT_PERIODONTAL_DATA;
	}

	const distoBuccalMargin = periodontal.distoBuccal.margin ?? 0;
	const buccalMargin = periodontal.buccal?.margin ?? 0;
	const mesioBuccalMargin = periodontal.mesioBuccal?.margin ?? 0;
	const distoPalatalMargin = periodontal.distoPalatal?.margin ?? 0;
	const palatalMargin = periodontal.palatal?.margin ?? 0;
	const mesioPalatalMargin = periodontal.mesioPalatal?.margin ?? 0;
	const distoBuccalDepth = periodontal.distoBuccal?.depth ?? 0;
	const buccalDepth = periodontal.buccal?.depth ?? 0;
	const mesioBuccalDepth = periodontal.mesioBuccal?.depth ?? 0;
	const distoPalatalDepth = periodontal?.distoPalatal?.depth ?? 0;
	const palatalDepth = periodontal?.palatal?.depth ?? 0;
	const mesioPalatalDepth = periodontal?.mesioPalatal?.depth ?? 0;
	const distoLingualMargin = periodontal?.distoLingual?.margin ?? 0;
	const lingualMargin = periodontal?.lingual?.margin ?? 0;
	const mesioLingualMargin = periodontal?.mesioLingual?.margin ?? 0;
	const distoLingualDepth = periodontal?.distoLingual?.depth ?? 0;
	const lingualDepth = periodontal?.lingual?.depth ?? 0;
	const mesioLingualDepth = periodontal?.mesioLingual?.depth ?? 0;

	// define order of values based on tooth side and view
	const valuesMap: ValuesMap = useMemo(() => {
		return {
			margin: {
				[ToothSide.Left]: {
					[ToothView.BUCCAL]: [
						distoBuccalMargin,
						buccalMargin,
						mesioBuccalMargin,
					],
					[ToothView.LINGUAL]: [
						distoLingualMargin,
						lingualMargin,
						mesioLingualMargin,
					],
					[ToothView.PALATAL]: [
						distoPalatalMargin,
						palatalMargin,
						mesioPalatalMargin,
					],
				},
				[ToothSide.Right]: {
					[ToothView.BUCCAL]: [
						mesioBuccalMargin,
						buccalMargin,
						distoBuccalMargin,
					],
					[ToothView.LINGUAL]: [
						mesioLingualMargin,
						lingualMargin,
						distoLingualMargin,
					],
					[ToothView.PALATAL]: [
						mesioPalatalMargin,
						palatalMargin,
						distoPalatalMargin,
					],
				},
			},
			depth: {
				[ToothSide.Left]: {
					[ToothView.BUCCAL]: [distoBuccalDepth, buccalDepth, mesioBuccalDepth],
					[ToothView.LINGUAL]: [
						distoLingualDepth,
						lingualDepth,
						mesioLingualDepth,
					],
					[ToothView.PALATAL]: [
						distoPalatalDepth,
						palatalDepth,
						mesioPalatalDepth,
					],
				},
				[ToothSide.Right]: {
					[ToothView.BUCCAL]: [mesioBuccalDepth, buccalDepth, distoBuccalDepth],
					[ToothView.LINGUAL]: [
						mesioLingualDepth,
						lingualDepth,
						distoLingualDepth,
					],
					[ToothView.PALATAL]: [
						mesioPalatalDepth,
						palatalDepth,
						distoPalatalDepth,
					],
				},
			},
		};
	}, [periodontal]);

	const margins = useMemo(() => {
		return valuesMap.margin[side][view];
	}, [periodontal, view, side]);

	const depths = useMemo(() => {
		return valuesMap.depth[side][view];
	}, [periodontal, view, side]);

	return { margins, depths };
}
